import {
  Entity,
  EntityLink, RELATION_HAS_PART, RELATION_PROVIDES_API,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { EntityRefLinks, useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import {
  HeaderIconLinkRow,
  InfoCard,
  IconLinkVerticalProps,
} from '@backstage/core-components';

import { Typography, Link, Box, Grid } from '@material-ui/core';
import DashboardIcon from '@material-ui/icons/Dashboard';
import GraphicEqIcon from '@material-ui/icons/GraphicEq';
import SubjectIcon from '@material-ui/icons/Subject';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import HttpsIcon from '@material-ui/icons/Https';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import AddAlertIcon from '@material-ui/icons/AddAlert';
import { MissingLink } from './MissingLink';
import { usePermission } from '@backstage/plugin-permission-react';
import { frontendComponentNotificationPermission } from '@zeiss/backstage-plugin-frontend-permissions-common';
import { useEffect, useState } from 'react';

type ObservabilityTelemetryType = {
  dashboardWorkspace?: EntityLink;
  tracingWorkspace?: EntityLink;
  loggingWorkspace?: EntityLink;
  incidentPlaybooks?: EntityLink;
  tykApiWorkspace?: EntityLink;
  itsmTicketCreate?: EntityLink;
};

type ExtendedEntity = Entity & {
  spec: {
    type: string;
    zeiss?: {
      observability?: {
        componentowner?: string;
        operationsowner?: string;
      };
    };
  };
};

export const useObservabilityTelemetry = ({
  entity,
}: {
  entity: Entity;
}): ObservabilityTelemetryType => {
  return {
    dashboardWorkspace: entity.metadata.links?.find(
      link => link.type === 'dashboard-workspace',
    ),
    tracingWorkspace: entity.metadata.links?.find(
      link => link.type === 'tracing-workspace',
    ),
    loggingWorkspace: entity.metadata.links?.find(
      link => link.type === 'logging-workspace',
    ),
    incidentPlaybooks: entity.metadata.links?.find(
      link => link.type === 'incident-playbooks',
    ),
    tykApiWorkspace: entity.metadata.links?.find(
      link => link.type === 'tyk-api-workspace',
    ),
    itsmTicketCreate: entity.metadata.links?.find(
      link => link.type === 'itsm-ticket-create',
    ),
  };
};

const hasProvidedApis = (entities: Entity[]) => {
  return entities
    ?.flatMap(value => value.relations)
    .some(value => value?.type === RELATION_PROVIDES_API) || false;
};

const hasStatusIoAnnotations = (entity: Entity) => {
  const annotations = entity?.metadata?.annotations;

  // if entity is a system then  only a component-id is required
  if (entity.kind === 'System') {
    return annotations?.hasOwnProperty('zeiss.status.io/component-id') || false;
  }

  // otherwise the container-id is required additionally
  return (
    (annotations?.hasOwnProperty('zeiss.status.io/component-id') &&
      annotations?.hasOwnProperty('zeiss.status.io/container-id')) ||
    false
  );
};

export const ObservabilityTelemetryCard = () => {
  const [isUserEntityOwner, setIsUserEntityOwner] = useState(false);

  const { entity } = useEntity() as { entity: ExtendedEntity };

  const entities = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: "Component",
  }).entities || [];

  const provideApis =
    (entity?.spec?.type === 'service' && hasProvidedApis([entity])) ||
    (entity?.kind === 'System' && hasProvidedApis(entities));

  const isCreateNotificationAvailable =
    (provideApis || hasStatusIoAnnotations(entity)) && isUserEntityOwner;
  const resourceRef = stringifyEntityRef(entity).trim();

  const entityOwnerPermissionResult = usePermission({
    permission: frontendComponentNotificationPermission,
    resourceRef,
  });

  useEffect(() => {
    if (entityOwnerPermissionResult) {
      setIsUserEntityOwner(entityOwnerPermissionResult.allowed);
    }
  }, [entityOwnerPermissionResult]);

  const {
    dashboardWorkspace,
    tracingWorkspace,
    loggingWorkspace,
    incidentPlaybooks,
    tykApiWorkspace,
    itsmTicketCreate,
  } = useObservabilityTelemetry({ entity });

  if (entity?.spec?.type === 'service' || entity?.spec?.type === 'website' || entity?.kind === 'System') {
    if (
      !dashboardWorkspace &&
      !tracingWorkspace &&
      !loggingWorkspace &&
      !incidentPlaybooks &&
      !tykApiWorkspace &&
      !itsmTicketCreate &&
      !provideApis &&
      !hasStatusIoAnnotations(entity)
    ) {
      return (
        <InfoCard title="Observability & Telemetry" variant="fullHeight">
          <MissingLink
            links={[
              { type: 'dashboard-workspace' },
              { type: 'tracing-workspace' },
              { type: 'logging-workspace' },
              { type: 'incident-playbooks' },
              { type: 'tyk-api-workspace' },
              { type: 'itsm-ticket-create' },
            ]}
            entity={entity}
          />
        </InfoCard>
      );
    }

    const links = [
      {
        label: 'Dashboard',
        disabled: !dashboardWorkspace?.url,
        href: dashboardWorkspace?.url ?? '',
        icon: <DashboardIcon />,
      },
      {
        label: 'Tracing',
        disabled: !tracingWorkspace?.url,
        href: tracingWorkspace?.url ?? '',
        icon: <GraphicEqIcon />,
      },
      {
        label: 'Logging',
        disabled: !loggingWorkspace?.url,
        href: loggingWorkspace?.url ?? '',
        icon: <SubjectIcon />,
      },
      {
        label: 'Incident Playbooks',
        disabled: !incidentPlaybooks?.url,
        href: incidentPlaybooks?.url ?? '',
        icon: <MenuBookIcon />,
      },
      tykApiWorkspace?.url && {
        label: 'Tyk API',
        disabled: !tykApiWorkspace?.url,
        href: tykApiWorkspace?.url ?? '',
        icon: <HttpsIcon />,
      },
      {
        label: 'ITSM Ticket',
        disabled: !itsmTicketCreate?.url,
        href: itsmTicketCreate?.url ?? '',
        icon: <PlaylistAddIcon />,
      },
      {
        label: 'Create Notification',
        disabled: !isCreateNotificationAvailable,
        href: './create-notification',
        icon: <AddAlertIcon />,
      },
    ].filter(Boolean) as IconLinkVerticalProps[];

    const componentOwner = entity.spec.zeiss?.observability?.componentowner;
    const operationsOwner = entity.spec.zeiss?.observability?.operationsowner;

    return (
      <InfoCard title="Observability & Telemetry" variant="fullHeight">
        <Box display="flex" flexDirection="column" flexWrap="wrap" gridGap={8}>
          <HeaderIconLinkRow links={links} />
          {!isCreateNotificationAvailable && (
            <Link href="https://developer.zeiss.com/docs/default/system/zeiss-developer-portal/recommendations/">
              Learn more how to configure notifications
            </Link>
          )}
        </Box>
        <Grid container spacing={1}>
          {componentOwner && (
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">Component-Owner</Typography>
              <EntityRefLinks
                entityRefs={[componentOwner]}
                defaultKind="User"
              />
            </Grid>
          )}
          {operationsOwner && (
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">SRE-Operations-Owner</Typography>
              <EntityRefLinks
                entityRefs={[operationsOwner]}
                defaultKind="User"
              />
            </Grid>
          )}
        </Grid>
      </InfoCard>
    );
  }
  return null;
};
